import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ModuleFrame, { KV } from '../ModuleFrame';
import { withApp } from '../AppSelector';
import { InstanceLinkSelect } from '../InstanceLink';
import { AgentInstall } from '../AgentEditor';
import NativeApps from '../NativeApps';
import McpClients from '../McpClients';
import ConceptScreens from '../ConceptScreens';
import { apiPost, isBackendConfigured } from '../../utils/backendApi';
import { updateEntity, friendlyError } from '../../data/entities';

// Lab › Surfaces — where the app lives (was App Builder's Surfaces tab):
// the website script, iframe and API per agent; the hosted instance
// (link, sync, drift); native apps; AI clients (MCP); intended channels.

const CHANNELS = ['web', 'widget', 'voice', 'mobile'];

export default function Surfaces({ org, app, studio, onPhase }) {
  const { attached, kLive, instance, instances, can, canEdit, isAdmin } = studio;
  return (
    <div className="lab-studio" data-nova-note={`Surfaces phase for "${app.name}": ${instance ? `hosted instance ${instance.slug} (${instance.status})` : 'no hosted instance yet'}, ${attached.length} agent${attached.length === 1 ? '' : 's'} with website script / iframe / API, ${kLive.length} live docs. NoVA: deploy_instance (the person confirms), sync_instance, instance_status; embed snippets are copied here.`}>
      <ConceptScreens org={org} appId={app.id} canRevise={can('lab.run')} onOpen={(screen, o) => onPhase('concept', null, { fig: 'screens', screen, revise: o?.revise ? 1 : null })} />
      <ModuleFrame.Columns cols={2}>
        {attached.map((a) => (
          <ModuleFrame.Panel key={a.id} title={a.name} meta="website script · iframe · API">
            <AgentInstall org={org} agent={a} canEdit={can('agent.update')} />
          </ModuleFrame.Panel>
        ))}
        {!attached.length && (
          <ModuleFrame.Panel title="Website, iframe, API" meta="needs an agent">
            <p className="cs-help">Attach an agent in Design — each one comes with a script tag for any website, an iframe, and an API.</p>
          </ModuleFrame.Panel>
        )}
        <div id="ab-hosted">
          <ModuleFrame.Panel title="Hosted app" meta={instance ? instance.slug : 'no hosted instance linked'}>
            <InstancePanel org={org} app={app} instance={instance} instances={instances} attached={attached} kLive={kLive} canEdit={can('instance.sync')} canLink={canEdit} />
          </ModuleFrame.Panel>
        </div>
        {instance ? (
          <>
            <ModuleFrame.Panel title="Native apps" meta="iOS · Android · macOS · Windows">
              <NativeApps org={org} app={app} instance={instance} isAdmin={isAdmin} />
            </ModuleFrame.Panel>
            <ModuleFrame.Panel title="AI clients (MCP)" meta="Cursor · Claude · ChatGPT">
              <McpClients org={org} app={app} instance={instance} />
            </ModuleFrame.Panel>
            <ModuleFrame.Panel title="Intended channels" meta="for planning">
              <ChannelsForm orgId={org.id} app={app} canEdit={canEdit} />
            </ModuleFrame.Panel>
          </>
        ) : (
          <details className="life-more">
            <summary>More ways to ship</summary>
            <div className="life-more-grid">
              <ModuleFrame.Panel title="Native apps" meta="needs a hosted instance">
                <NativeApps org={org} app={app} instance={instance} isAdmin={isAdmin} />
              </ModuleFrame.Panel>
              <ModuleFrame.Panel title="AI clients (MCP)" meta="needs a hosted instance">
                <McpClients org={org} app={app} instance={instance} />
              </ModuleFrame.Panel>
              <ModuleFrame.Panel title="Intended channels" meta="for planning">
                <ChannelsForm orgId={org.id} app={app} canEdit={canEdit} />
              </ModuleFrame.Panel>
            </div>
          </details>
        )}
      </ModuleFrame.Columns>
    </div>
  );
}

export function InstancePanel({ org, app, instance, instances, attached, kLive, canEdit, canLink }) {
  const sync = app.links?.instanceSync || null;
  const [state, setState] = useState('');
  const doSync = async () => {
    setState('syncing');
    try {
      await apiPost(`/apps/${org.id}/${app.id}/sync-instance`, {});
      setState('done');
    } catch (ex) {
      setState(friendlyError(ex));
    }
  };
  // Drift: anything the bundle carries edited after the last sync.
  const changedSince = sync ? [...attached, ...kLive].some((x) => (x.updatedAt || x.createdAt || 0) > sync.at) : false;
  if (!instance) {
    return (
      <>
        <p className="mf-muted" style={{ margin: 0 }}>
          A hosted app is a dedicated instance with its own web address, database and users (App Kit). Its agent and knowledge are synced down from this design on every release, and it also knows its own users, records and features.
        </p>
        <div className="ab-next-actions" style={{ marginTop: 10 }}>
          <InstanceLinkSelect orgId={org.id} app={app} instances={instances} canEdit={canLink} />
          <Link to={withApp(`/orgs/${org.id}/deployments`, app)} className="cs-btn cs-btn-outline cs-btn-sm">Launch one in DevOps</Link>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="ab-next-actions" style={{ marginBottom: 10 }}>
        <InstanceLinkSelect orgId={org.id} app={app} instances={instances} canEdit={canLink} />
      </div>
      <KV rows={[
        ['Address', instance.urls?.app ? <a href={instance.urls.app} target="_blank" rel="noreferrer">{instance.urls.app}</a> : '—'],
        ['Status', instance.status || '—'],
        ['Last sync', sync ? `${new Date(sync.at).toLocaleString()} · ${sync.agents} agent${sync.agents === 1 ? '' : 's'}, ${sync.docs} document${sync.docs === 1 ? '' : 's'}` : 'never — the instance does not have this design yet'],
        ['State', !sync ? '—' : changedSince ? 'design changed since the last sync' : 'in sync'],
      ]} />
      {sync?.warnings?.length > 0 && (
        <ul className="mf-list" style={{ marginTop: 8 }}>
          {sync.warnings.map((w) => <li key={w} className="cs-help">{w}</li>)}
        </ul>
      )}
      <p className="cs-help" style={{ marginTop: 8 }}>
        Syncs automatically on every release from Deployments and when you build on the instance from Ready to ship. The instance's agent also knows that app's own screens, records and users.
      </p>
      <div className="ab-next-actions" style={{ marginTop: 10, alignItems: 'center' }}>
        {canEdit && isBackendConfigured() && (
          <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={state === 'syncing'} onClick={doSync}>{state === 'syncing' ? 'Syncing… (about a minute)' : 'Sync to instance now'}</button>
        )}
        <Link to={withApp(`/orgs/${org.id}/deployments`, app)} className="cs-btn cs-btn-outline cs-btn-sm">Deployments</Link>
        {state === 'done' && <span className="mf-muted">Synced.</span>}
        {state && state !== 'syncing' && state !== 'done' && <span className="cs-form-error">{state}</span>}
      </div>
    </>
  );
}

function ChannelsForm({ orgId, app, canEdit }) {
  const channels = app.channels || ['web'];
  const toggle = (c) => updateEntity('app', { org: orgId }, app.id, { channels: channels.includes(c) ? channels.filter((x) => x !== c) : [...channels, c] }).catch(() => {});
  return (
    <>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {CHANNELS.map((c) => (
          <button key={c} type="button" className={`cs-btn cs-btn-sm ${channels.includes(c) ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => toggle(c)} disabled={!canEdit}>{c}</button>
        ))}
      </div>
      <p className="cs-help" style={{ marginTop: 8 }}>Where you intend this app to be used. The script and iframe above cover web and widget; voice and mobile ride the same agent through the API.</p>
    </>
  );
}
